import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { deleteExclusionAreas } from '../../services/farmMapping/exclusionAreas/deleteExclusionAreas'
import { getExclusionAreasByFarm } from '../../services/farmMapping/exclusionAreas/getExclusionAreas'
import {
  createExclusionAreasFeatureByFarm,
  updateExclusionAreasFeatureByFarm,
} from '../../services/farmMapping/exclusionAreas/saveExclusionAreas'
import { errors, isWriteOutcomeUnknown } from '../../services/ruutsApi/errors'
import type { ExclusionAreaGeoJSON, RefData } from '../../types/apiModels'
import { queryKeys } from './queryKeys'

type ExclusionAreaFeaturesInput = {
  features: ExclusionAreaGeoJSON[]
  exclusionTypes: RefData[]
}

// The accordion renders every write failure itself (blocked-period detail modal, otherwise a local
// snackbar), so the global bridge must stay silent to keep exactly one error surface.
const suppressGlobalError = { suppressGlobalError: true }

// Every exclusion-area write makes the backend recrop the farm's paddocks and strata and rebind its
// monitoring sites; the project area and QU hints are derived from those geometries and go stale too.
const invalidateReprocessedFarmEntities = (queryClient: QueryClient, farmId: string | undefined) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.exclusionAreas(farmId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(farmId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.paddocks(farmId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.paddocksByIdsRoot }),
    queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farmId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.projectArea(farmId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.quHintsRoot(farmId) }),
  ])

// Only an unknown outcome refetches: a 4xx or an API 500 is a rejected write that changed nothing
// server-side. Not awaited, so the error never waits on a slow or retried GET.
const refetchWhen =
  (isStale: (error: unknown) => boolean) =>
  (queryClient: QueryClient, farmId: string | undefined, error: unknown) => {
    if (isStale(error)) {
      invalidateReprocessedFarmEntities(queryClient, farmId)
    }
  }

const refetchWhenOutcomeUnknown = refetchWhen(isWriteOutcomeUnknown)

// Delete has no draft, and both DELETE endpoints answer 500 when nothing matches, so the area may
// already be gone.
const refetchAfterDeleteFailure = refetchWhen(
  error => error instanceof errors.UnexpectedEntityError,
)

export const useExclusionAreas = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    queryKey: queryKeys.exclusionAreas(farmId),
    queryFn: ({ signal }) => getExclusionAreasByFarm({ farmId: farmId as string, signal }),
    enabled: !!farmId,
  })

export const useCreateExclusionAreas = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ features, exclusionTypes }: ExclusionAreaFeaturesInput) =>
      createExclusionAreasFeatureByFarm({ farmId: farmId as string, features, exclusionTypes }),
    onSuccess: () => invalidateReprocessedFarmEntities(queryClient, farmId),
    onError: error => refetchWhenOutcomeUnknown(queryClient, farmId, error),
    meta: suppressGlobalError,
  })
}

// PUT /v1/exclusionAreas answers message-only (no payload): the fresh list can only
// come from an invalidate-refetch, never from seeding the cache with the response.
export const useUpdateExclusionAreas = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ features, exclusionTypes }: ExclusionAreaFeaturesInput) =>
      updateExclusionAreasFeatureByFarm({ farmId: farmId as string, features, exclusionTypes }),
    onSuccess: () => invalidateReprocessedFarmEntities(queryClient, farmId),
    onError: error => refetchWhenOutcomeUnknown(queryClient, farmId, error),
    meta: suppressGlobalError,
  })
}

export const useDeleteExclusionAreas = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entities }: { entities: { id: string }[] }) =>
      deleteExclusionAreas({ entities }),
    onSuccess: () => invalidateReprocessedFarmEntities(queryClient, farmId),
    onError: error => refetchAfterDeleteFailure(queryClient, farmId, error),
    meta: suppressGlobalError,
  })
}
